import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Plus, Save, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { MediaField, GalleryField } from "./MediaFields";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BLANK = {
  org: "",
  role: "",
  period: "",
  location: "",
  tags: [],
  bullets: [],
  order: 0,
  published: true,
  kind: "paid",
  hero_image: null,
  gallery: [],
  org_logo: null,
  org_url: "",
  org_link_label: "",
};

export default function AdminExperience() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/experiences`, { withCredentials: true });
      setItems(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async (payload) => {
    try {
      if (payload.id) {
        const { id, ...body } = payload;
        await axios.patch(`${API}/admin/experiences/${id}`, body, { withCredentials: true });
        toast.success("Updated");
      } else {
        await axios.post(`${API}/admin/experiences`, payload, { withCredentials: true });
        toast.success("Created");
      }
      setEditing(null);
      setCreating(false);
      load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(Array.isArray(d) ? d.map(x => x.msg).join(", ") : d || "Save failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this experience row?")) return;
    try {
      await axios.delete(`${API}/admin/experiences/${id}`, { withCredentials: true });
      setItems((prev) => prev.filter((c) => c.id !== id));
      toast.success("Deleted");
    } catch {
      toast.error("Delete failed");
    }
  };

  const paidCount = items.filter((c) => (c.kind || "paid") === "paid").length;
  const volCount = items.filter((c) => c.kind === "volunteer").length;

  return (
    <div data-testid="admin-experience">
      <div className="flex items-baseline justify-between flex-wrap gap-4">
        <div>
          <p className="eyebrow">Content</p>
          <h1 className="mt-3 font-display text-4xl md:text-5xl leading-tight tracking-tight">Experience</h1>
          <p className="mt-3 text-[#A9A9A6] text-sm max-w-lg">
            Career timeline + Beyond-the-CV volunteer roles. Order 1 = most recent inside each group.
            <span className="block mt-1 text-[11px] tracking-widest uppercase text-[#D8A936]">
              {paidCount} paid · {volCount} volunteer
            </span>
          </p>
        </div>
        <button
          onClick={() => { setCreating(true); setEditing({ ...BLANK, order: items.length + 1 }); }}
          data-testid="exp-add"
          className="btn-primary"
        >
          <Plus size={14} /> New role
        </button>
      </div>

      {loading ? (
        <div className="mt-10 text-[#A9A9A6]">Loading…</div>
      ) : (
        <div className="mt-8 border border-[#343437]">
          {items.length === 0 && (
            <div className="p-8 text-[#A9A9A6] text-sm">No roles yet.</div>
          )}
          {items.map((c) => (
            <div
              key={c.id}
              className="border-t border-[#343437] first:border-t-0 p-5 lg:p-6 grid grid-cols-1 lg:grid-cols-[80px_1fr_120px_140px_180px] gap-4 items-center"
              data-testid={`exp-row-${c.id}`}
            >
              <span className="oversize text-2xl text-[#D8A936]">{String(c.order).padStart(2, "0")}</span>
              <div>
                <p className="font-display text-[17px] text-[#FAF9F6]">{c.role}</p>
                <p className="text-xs text-[#A9A9A6] mt-1">{c.org} · {c.period}</p>
              </div>
              <span className={`text-[10px] tracking-[0.22em] uppercase ${c.kind === "volunteer" ? "text-[#7EC8B5]" : "text-[#A9A9A6]"}`}>
                {c.kind === "volunteer" ? "Volunteer" : "Paid"}
              </span>
              <span className={`text-[10px] tracking-[0.22em] uppercase inline-flex items-center gap-1.5 ${c.published ? "text-[#D8A936]" : "text-[#A9A9A6]"}`}>
                {c.published ? "Published" : "Unpublished"}
                {(c.hero_image || (c.gallery || []).length > 0 || c.org_logo) && (
                  <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#D8A936]" title="Has media" />
                )}
              </span>
              <div className="flex gap-2 justify-end">
                <button
                  onClick={() => setEditing(c)}
                  data-testid={`exp-edit-${c.id}`}
                  className="px-3 py-1.5 border border-[#343437] text-xs tracking-widest uppercase hover:border-[#D8A936] hover:text-[#D8A936] transition-colors"
                >
                  Edit
                </button>
                <button
                  onClick={() => remove(c.id)}
                  data-testid={`exp-delete-${c.id}`}
                  className="p-1.5 border border-[#343437] hover:border-red-400 hover:text-red-400 transition-colors"
                  aria-label="Delete"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <EditorModal
          initial={editing}
          isNew={creating}
          onCancel={() => { setEditing(null); setCreating(false); }}
          onSave={save}
        />
      )}
    </div>
  );
}

function EditorModal({ initial, isNew, onCancel, onSave }) {
  const [form, setForm] = useState({
    ...initial,
    tags: Array.isArray(initial.tags) ? initial.tags.join(", ") : (initial.tags || ""),
    bullets: Array.isArray(initial.bullets) ? initial.bullets.join("\n") : (initial.bullets || ""),
    hero_image: initial.hero_image ?? null,
    gallery: Array.isArray(initial.gallery) ? initial.gallery : [],
    org_logo: initial.org_logo ?? null,
    org_url: initial.org_url ?? "",
    org_link_label: initial.org_link_label ?? "",
    kind: initial.kind || "paid",
  });

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
      bullets: form.bullets.split("\n").map((b) => b.trim()).filter(Boolean),
      order: Number(form.order) || 0,
      // Strip empty-src media items — schema requires src on every asset.
      gallery: (form.gallery || []).filter((g) => g && g.src),
      hero_image: form.hero_image && form.hero_image.src ? form.hero_image : null,
      org_logo: form.org_logo && form.org_logo.src ? form.org_logo : null,
      org_url: form.org_url || null,
      org_link_label: form.org_link_label || null,
    };
    onSave(payload);
  };

  return (
    <div className="fixed inset-0 z-[80] bg-[#0B0B0C]/85 backdrop-blur-sm flex items-start md:items-center justify-center p-4 overflow-auto">
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        className="bg-[#151517] border border-[#343437] w-full max-w-3xl p-8 space-y-5 relative my-8"
        data-testid="exp-editor"
      >
        <button type="button" onClick={onCancel} className="absolute top-4 right-4 text-[#A9A9A6] hover:text-[#FAF9F6]" aria-label="Close">
          <X size={18} />
        </button>
        <p className="eyebrow">{isNew ? "New" : "Edit"}</p>
        <h2 className="font-display text-2xl">Experience row</h2>

        <div className="grid grid-cols-1 md:grid-cols-[1fr_180px] gap-4">
          <TextField label="Role" value={form.role} onChange={(v) => set("role", v)} required testid="exp-role" />
          <label className="block">
            <span className="eyebrow block mb-2">Kind</span>
            <select
              value={form.kind}
              onChange={(e) => set("kind", e.target.value)}
              data-testid="exp-kind"
              className="w-full bg-transparent border border-[#343437] text-[#FAF9F6] px-3 py-2.5 outline-none focus:border-[#D8A936]"
            >
              <option value="paid" className="bg-[#151517]">Paid — Timeline</option>
              <option value="volunteer" className="bg-[#151517]">Volunteer — Beyond CV</option>
            </select>
          </label>
        </div>

        <TextField label="Organisation" value={form.org} onChange={(v) => set("org", v)} required testid="exp-org" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <TextField label="Period" value={form.period} onChange={(v) => set("period", v)} required testid="exp-period" />
          <TextField label="Location" value={form.location} onChange={(v) => set("location", v)} testid="exp-location" />
        </div>
        <TextField label="Tags (comma-separated)" value={form.tags} onChange={(v) => set("tags", v)} testid="exp-tags" />
        <TextArea label="Bullets (one per line)" rows={6} value={form.bullets} onChange={(v) => set("bullets", v)} testid="exp-bullets" />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <TextField label="Order" type="number" value={form.order} onChange={(v) => set("order", v)} testid="exp-order" />
          <label className="flex items-center gap-3 mt-6 text-sm text-[#FAF9F6]">
            <input
              type="checkbox"
              checked={form.published}
              onChange={(e) => set("published", e.target.checked)}
              className="accent-[#D8A936]"
              data-testid="exp-published"
            />
            Published (visible on About page)
          </label>
        </div>

        {/* Media & link fields — new in the Volunteer CMS extension */}
        <div className="pt-4 mt-4 border-t border-[#343437] space-y-4">
          <p className="eyebrow text-[#D8A936]">Media & organisation link</p>
          <p className="text-xs text-[#A9A9A6]">Empty fields render nothing (§3.2 empty-state rule). Volunteer roles show the gallery under the role; paid roles show the org logo next to the org name.</p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <TextField label="Org URL (optional)" value={form.org_url} onChange={(v) => set("org_url", v)} testid="exp-org-url" />
            <TextField label='Link label (e.g. "Visit site")' value={form.org_link_label} onChange={(v) => set("org_link_label", v)} testid="exp-link-label" />
          </div>

          <MediaField
            label="Org logo (optional)"
            value={form.org_logo}
            onChange={(v) => set("org_logo", v)}
            testid="exp-org-logo"
            aspect="aspect-[3/2]"
          />

          <MediaField
            label="Hero image (optional)"
            value={form.hero_image}
            onChange={(v) => set("hero_image", v)}
            testid="exp-hero"
            aspect="aspect-[16/9]"
          />

          <GalleryField
            label="Photo gallery"
            value={form.gallery}
            onChange={(v) => set("gallery", v)}
            testid="exp-gallery"
          />
        </div>

        <div className="flex justify-end gap-3 pt-2">
          <button type="button" onClick={onCancel} className="btn-ghost">Cancel</button>
          <button type="submit" data-testid="exp-save" className="btn-primary">
            <Save size={14} /> {isNew ? "Create" : "Save"}
          </button>
        </div>
      </form>
    </div>
  );
}

function TextField({ label, value, onChange, type = "text", required, testid }) {
  return (
    <label className="block">
      <span className="eyebrow block mb-2">{label}{required && <span className="text-[#D8A936]"> *</span>}</span>
      <input
        type={type}
        required={required}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="w-full bg-transparent border border-[#343437] text-[#FAF9F6] px-3 py-2.5 outline-none focus:border-[#D8A936]"
      />
    </label>
  );
}

function TextArea({ label, value, onChange, required, testid, rows = 4 }) {
  return (
    <label className="block">
      <span className="eyebrow block mb-2">{label}{required && <span className="text-[#D8A936]"> *</span>}</span>
      <textarea
        rows={rows}
        required={required}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="w-full bg-transparent border border-[#343437] text-[#FAF9F6] px-3 py-2.5 outline-none focus:border-[#D8A936] resize-none"
      />
    </label>
  );
}
